import { showConfirm } from '../components/ConfirmDialog';
import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useDenseTableLabels } from '../hooks/useDenseTableLabels';
import { useTranslation } from 'react-i18next';
import { useProfileContext } from '../hooks/useProfileContext';
import {
  apiGetSongs,
  apiCreateSong,
  apiUpdateSong,
  apiDeleteSong,
  apiReorderSongs,
  apiGetSongCharts,
  apiUpsertSongChart,
  apiDeleteSongChart,
  type SongDTO,
  type SongChart,
} from '../api';
import { reportSaveError, reportSaveOk } from '../lib/saveStatus';
import { useWriteGate } from '../lib/writeGate';
import AutoTextarea from '../components/AutoTextarea';
import PageHeader from '../components/PageHeader';
import { withPageIcon } from '../lib/uiIcons';

/** Empty draft for the "+ Add song" modal. The artist defaults to the active
 *  band's name (the songbook is per-band, so most songs are by the band). */
function newSongDraft(bandName?: string): SongDTO {
  return { id: crypto.randomUUID(), band_id: '', title: '', artist: bandName || '', position: 0 };
}

type ChartFormMode = 'add' | 'edit';

export default function SongbookPage() {
  const denseRef = useRef<HTMLDivElement | null>(null);
  useDenseTableLabels(denseRef);
  const { t } = useTranslation();
  const { activeProfileId, profiles } = useProfileContext();
  const activeBandName = profiles.find((p) => p.id === activeProfileId)?.name || '';

  const [songs, setSongs] = useState<SongDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  // List controls
  const [search, setSearch] = useState('');
  const [sortAz, setSortAz] = useState(false);

  // Modal (create / edit — explicit Save, no autosave)
  const [modalOpen, setModalOpen] = useState(false);
  const [isNew, setIsNew] = useState(false);
  const [draft, setDraft] = useState<SongDTO>(newSongDraft());
  const [saving, setSaving] = useState(false);

  // Drag-to-reorder (TeamPage pattern)
  const [dragIdx, setDragIdx] = useState<number | null>(null);

  // ---- Charts par instrument (Smart Setlist V2) ----
  // Per-song charts, aggregated when the songbook loads so the list can show a
  // badge (song has ≥1 chart) and offer the "filter by instrument" dropdown.
  const [chartsBySong, setChartsBySong] = useState<Record<string, SongChart[]>>({});
  // '' = show every song; otherwise only songs with a chart for this instrument.
  const [filterInstrument, setFilterInstrument] = useState('');
  // The song whose Charts panel is open.
  const [selectedSong, setSelectedSong] = useState<SongDTO | null>(null);
  // Add / edit form state inside the Charts panel.
  const [cMode, setCMode] = useState<ChartFormMode>('add');
  const [cShowForm, setCShowForm] = useState(false);
  const [cInstrument, setCInstrument] = useState('');
  const [cContent, setCContent] = useState('');
  const [cSaving, setCSaving] = useState(false);
  const [cErr, setCErr] = useState<string | null>(null);

  // Role gate: viewers can read, editor+ can write (server enforces too).
  // Capability-aware gate (lot A2): `/api/bands/:id/songs` is mounted AFTER the
  // server's global read-only guard (server/index.js:519-525; guarded mount at
  // :542) → a global read-only account (even a band editor) must not attempt a
  // write here. (Conditional pre-guard mount at :460-465 exempts holders of
  // `content.songbook` — flipped in the A2-CAP pass (2026-09-25) to the exact
  // capability (aliased as `canWriteContent`): a read-only member GRANTED
  // `content.songbook` writes here, one without the grant stays frozen.)
  const writeGate = useWriteGate(activeProfileId);
  // A2-CAP: content.songbook
  const canWriteContent = writeGate.canWriteCap('content.songbook');
  // The note describes the page's REAL state: it must NOT tell a globally
  // read-only account that editing is disabled while the capability that
  // unlocks THIS page is held (the fields are open). Show it only when the
  // page's own write predicate is denied.
  const readOnlyNote = writeGate.readOnlyReason === 'global-read-only' && !canWriteContent;

  const load = useCallback(() => {
    const band = activeProfileId;
    if (!band) return;
    setLoading(true);
    setLoadError(null);
    apiGetSongs(band)
      .then(async (data) => {
        setSongs(data);
        // Fetch every song's charts (badge + filter dropdown + panel). Failures
        // are tolerated: a single chart fetch error must not block the list.
        const settled = await Promise.allSettled(
          data.map((s) => apiGetSongCharts(band, s.id)),
        );
        const agg: Record<string, SongChart[]> = {};
        data.forEach((s, i) => {
          agg[s.id] = settled[i].status === 'fulfilled' ? (settled[i].value || []) : [];
        });
        setChartsBySong(agg);
      })
      .catch((err) => {
        const msg = err instanceof Error ? err.message : String(err);
        setLoadError(msg);
        reportSaveError('songbook', err);
      })
      .finally(() => setLoading(false));
  }, [activeProfileId, reloadKey]);

  useEffect(() => { load(); }, [load]);

  // Union of instrument names that currently have a chart anywhere in the
  // songbook (sorted, case-insensitive) → filter dropdown options + datalist.
  const chartInstruments = useMemo(() => {
    const set = new Set<string>();
    Object.values(chartsBySong).forEach((list) => list.forEach((c) => { if (c.instrument) set.add(c.instrument); }));
    return [...set].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [chartsBySong]);

  const chartsFor = (songId: string): SongChart[] => chartsBySong[songId] ?? [];
  const hasChartForInstrument = (songId: string, instrument: string): boolean =>
    chartsFor(songId).some((c) => c.instrument === instrument);

  // If the selected instrument no longer has any chart, reset the filter.
  useEffect(() => {
    if (loading) return;
    if (filterInstrument && !chartInstruments.includes(filterInstrument)) setFilterInstrument('');
  }, [filterInstrument, chartInstruments, loading]);

  // Reordering only makes sense on the FULL songbook list (no search, no A–Z,
  // no instrument filter). The i18n key reorderDisabledHint explains why.
  // A2-CAP: content.songbook
  const reorderable = canWriteContent && !search.trim() && !sortAz && !filterInstrument;

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    let filtered = songs;
    if (q) {
      filtered = filtered.filter((s) =>
        (s.title || '').toLowerCase().includes(q) ||
        (s.artist || '').toLowerCase().includes(q));
    }
    if (filterInstrument) {
      filtered = filtered.filter((s) => hasChartForInstrument(s.id, filterInstrument));
    }
    if (sortAz) {
      return [...filtered].sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    }
    return [...filtered].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  }, [songs, search, sortAz, filterInstrument, chartsBySong]);

  const openAdd = () => {
    setDraft(newSongDraft(activeBandName));
    setIsNew(true);
    setModalOpen(true);
  };

  const openEdit = (song: SongDTO) => {
    setDraft({ ...song });
    setIsNew(false);
    setModalOpen(true);
  };

  const saveDraft = async () => {
    // A2-CAP: content.songbook
    if (!canWriteContent) return;
    if (!activeProfileId) return;
    if (!draft.title.trim()) return; // title is required
    setSaving(true);
    try {
      const payload = {
        title: draft.title.trim(),
        artist: draft.artist || '',
        keys: draft.keys || '',
        tempo: draft.tempo || '',
        duration: draft.duration || '',
        notes: draft.notes || '',
      };
      if (isNew) {
        await apiCreateSong(activeProfileId, payload);
      } else {
        await apiUpdateSong(activeProfileId, draft.id, payload);
      }
      reportSaveOk('songbook');
      setModalOpen(false);
      load();
    } catch (err) {
      reportSaveError('songbook', err);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (song: SongDTO) => {
    // A2-CAP: content.songbook
    if (!canWriteContent) return;
    if (!activeProfileId) return;
    if (!(await showConfirm(t('songbook.deleteConfirm', { title: song.title || '—' })))) return;
    try {
      await apiDeleteSong(activeProfileId, song.id);
      reportSaveOk('songbook');
      if (selectedSong?.id === song.id) setSelectedSong(null);
      load();
    } catch (err) {
      reportSaveError('songbook', err);
    }
  };

  // Optimistic local reorder + persist the new id order.
  const reorderSongs = useCallback((ids: string[]) => {
    // A2-CAP: content.songbook
    if (!canWriteContent) return;
    if (!activeProfileId) return;
    const orderMap = new Map(ids.map((id, i) => [id, i]));
    setSongs((prev) => prev.map((s) => (orderMap.has(s.id) ? { ...s, position: orderMap.get(s.id)! } : s)));
    apiReorderSongs(activeProfileId, ids).catch((err) => reportSaveError('songbook', err));
  }, [activeProfileId, canWriteContent]);

  const handleDragStart = useCallback((e: React.DragEvent, idx: number) => {
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent, dropIdx: number) => {
      e.preventDefault();
      if (dragIdx === null || dragIdx === dropIdx) return;
      const ids = visible.map((s) => s.id);
      const [moved] = ids.splice(dragIdx, 1);
      ids.splice(dropIdx, 0, moved);
      reorderSongs(ids);
      setDragIdx(null);
    },
    [dragIdx, visible, reorderSongs],
  );

  /* ---- Charts panel handlers (Smart Setlist V2) ---- */

  const openCharts = (song: SongDTO) => {
    setSelectedSong(song);
    setCMode('add');
    setCShowForm(false);
    setCInstrument('');
    setCContent('');
    setCErr(null);
  };

  const closeCharts = () => {
    if (cSaving) return;
    setSelectedSong(null);
  };

  const startAddChart = () => {
    setCMode('add');
    setCInstrument('');
    setCContent('');
    setCErr(null);
    setCShowForm(true);
  };

  const startEditChart = (chart: SongChart) => {
    setCMode('edit');
    setCInstrument(chart.instrument);
    setCContent(chart.content);
    setCErr(null);
    setCShowForm(true);
  };

  const saveChart = async () => {
    // A2-CAP: content.songbook
    if (!canWriteContent) return;
    if (!activeProfileId || !selectedSong) return;
    const instrument = cInstrument.trim();
    if (!instrument) { setCErr(t('songCharts.instrumentRequired')); return; }
    setCSaving(true);
    setCErr(null);
    try {
      await apiUpsertSongChart(activeProfileId, selectedSong.id, { instrument, content: cContent });
      reportSaveOk('songCharts');
      // Collapse the form back to the list; load() refreshes songs + charts.
      setCShowForm(false); setCMode('add'); setCInstrument(''); setCContent('');
      load();
    } catch (err) {
      reportSaveError('songCharts', err);
      setCErr(err instanceof Error ? err.message : String(err));
    } finally {
      setCSaving(false);
    }
  };

  const deleteChart = async (chart: SongChart) => {
    // A2-CAP: content.songbook
    if (!canWriteContent) return;
    if (!activeProfileId || !selectedSong) return;
    if (!(await showConfirm(t('songCharts.deleteConfirm', { instrument: chart.instrument, title: selectedSong.title || '—' })))) return;
    try {
      await apiDeleteSongChart(activeProfileId, selectedSong.id, chart.instrument);
      reportSaveOk('songCharts');
      setCShowForm(false); setCMode('add'); setCInstrument(''); setCContent('');
      load();
    } catch (err) {
      reportSaveError('songCharts', err);
    }
  };

  if (!activeProfileId) {
    return <div ref={denseRef} className="page"><div className="loading">{t('songbook.selectBandFirst')}</div></div>;
  }

  const openChartsList = selectedSong ? chartsFor(selectedSong.id) : [];

  return (
    <div className="page">
      <PageHeader
        title={withPageIcon('songbook.title', t('songbook.title'))}
        actions={
          // A2-CAP: content.songbook
          canWriteContent ? (
            <button className="btn btn-primary" onClick={openAdd}>
              {t('songbook.addSong')}
            </button>
          ) : null
        }
      />

      {readOnlyNote && (
        <p className="text-dim" style={{ color: 'var(--text-dim)', fontSize: 'var(--font-sm)', margin: '0 0 12px' }}>
          {t('common.readOnlyAccount')}
        </p>
      )}

      {/* Filters / toolbar */}
      <div className="filters" style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          className="form-input"
          style={{ maxWidth: 260 }}
          placeholder={t('songbook.searchPh')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label={t('songbook.searchPh')}
        />
        <button
          className={`btn ${sortAz ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setSortAz((v) => !v)}
          title={t('songbook.sortAz')}
        >
          {t('songbook.sortAz')}
        </button>
        {chartInstruments.length > 0 && (
          <select
            className="form-input"
            style={{ maxWidth: 240 }}
            aria-label={t('songCharts.filterByInstrument')}
            title={t('songCharts.filterByInstrument')}
            value={filterInstrument}
            onChange={(e) => { setFilterInstrument(e.target.value); setDragIdx(null); }}
          >
            <option value="">{t('songCharts.filterAll')}</option>
            {chartInstruments.map((inst) => (
              <option key={inst} value={inst}>{inst}</option>
            ))}
          </select>
        )}
        <span style={{ color: 'var(--text-dim)', fontSize: '0.85rem' }}>
          {t('songbook.count', { count: visible.length })}
        </span>
      </div>

      {loadError && (
        <div className="card" style={{ borderColor: 'var(--danger)', marginBottom: 16 }}>
          <strong style={{ color: 'var(--danger)' }}>{loadError}</strong>
          <div style={{ marginTop: 8 }}>
            <button className="btn btn-small" onClick={() => setReloadKey((k) => k + 1)}>{t('songbook.retry')}</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading">{t('songbook.loading')}</div>
      ) : songs.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '48px 24px' }}>
          <p style={{ color: 'var(--text-dim)' }}>{t('songbook.empty')}</p>
        </div>
      ) : visible.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '48px 24px' }}>
          <p style={{ color: 'var(--text-dim)' }}>
            {filterInstrument
              ? t('songCharts.filterNoMatches', { instrument: filterInstrument })
              : t('songbook.noMatches')}
          </p>
        </div>
      ) : (
        <div>
          {!reorderable && (
            <p style={{ color: 'var(--text-dim)', fontSize: '0.85rem', marginBottom: 8 }}>
              {t('songbook.reorderDisabledHint')}
            </p>
          )}
          <div className="table-wrapper">
            <table className="sb-table tbl-dense">
              <thead>
                <tr>
                  {reorderable && <th aria-label={t('songbook.dragToReorder')} />}
                  <th>{t('songbook.colTitle')}</th>
                  <th>{t('songbook.colArtist')}</th>
                  <th>{t('songbook.colDuration')}</th>
                  <th>{t('songbook.colKey')}</th>
                  <th>{t('songbook.colTempo')}</th>
                  <th>{t('songbook.colNotes')}</th>
                  <th>{t('songCharts.openCharts')}</th>
                  {canWriteContent && <th>{t('songbook.actions')}</th>}
                </tr>
              </thead>
              <tbody>
                {visible.map((song, idx) => {
                  const chartCount = chartsFor(song.id).length;
                  const hasChart = chartCount > 0;
                  return (
                    <tr
                      key={song.id}
                      draggable={reorderable}
                      onDragStart={reorderable ? (e) => handleDragStart(e, idx) : undefined}
                      onDragOver={reorderable ? handleDragOver : undefined}
                      onDrop={reorderable ? (e) => handleDrop(e, idx) : undefined}
                      onDragEnd={reorderable ? () => setDragIdx(null) : undefined}
                      className={dragIdx === idx ? 'dragging' : ''}
                    >
                      {reorderable && (
                        <td>
                          <span
                            className="sb-drag-handle"
                            draggable
                            onDragStart={(e) => handleDragStart(e, idx)}
                            onDragEnd={() => setDragIdx(null)}
                            title={t('songbook.dragToReorder')}
                            aria-label={t('songbook.dragToReorder')}
                            style={{ cursor: 'grab', color: 'var(--text-dim)', userSelect: 'none' }}
                          >
                            ⋮⋮
                          </span>
                        </td>
                      )}
                      <td style={{ fontWeight: 600 }}>
                        <button
                          type="button"
                          style={{ fontWeight: 600, textAlign: 'left', padding: 0, cursor: 'pointer', color: 'var(--text)', background: 'transparent', border: 'none' }}
                          onClick={() => openCharts(song)}
                          title={t('songCharts.openCharts')}
                        >
                          {song.title || '—'}
                        </button>
                      </td>
                      <td>{song.artist || ''}</td>
                      <td>{song.duration || ''}</td>
                      <td>{song.keys || ''}</td>
                      <td>{song.tempo || ''}</td>
                      <td>{song.notes || ''}</td>
                      <td>
                        {hasChart ? (
                          <button
                            type="button"
                            className="btn btn-small"
                            onClick={() => openCharts(song)}
                            title={t('songCharts.hasChart')}
                            aria-label={t('songCharts.hasChart')}
                            style={{ color: 'var(--text)' }}
                          >
                            ⚡ {chartCount}
                          </button>
                        ) : (
                          <button type="button" className="btn btn-small btn-secondary" onClick={() => openCharts(song)}>
                            {t('songCharts.openCharts')}
                          </button>
                        )}
                      </td>
                      {// A2-CAP: content.songbook
  canWriteContent && (
                        <td>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button className="btn btn-small btn-secondary" onClick={() => openEdit(song)}>
                              {t('songbook.edit')}
                            </button>
                            <button className="btn btn-small btn-danger" onClick={() => remove(song)}>
                              {t('songbook.delete')}
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add / Edit modal — explicit Save (no autosave) */}
      {modalOpen && (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-label={isNew ? t('songbook.addSong') : t('songbook.editSong')}
          onClick={(e) => { if (e.target === e.currentTarget) setModalOpen(false); }}
        >
          <div className="modal" style={{ maxWidth: 560 }}>
            <div className="modal-header">
              <h3 className="modal-title">{isNew ? t('songbook.addSong') : t('songbook.editSong')}</h3>
              <button type="button" className="modal-close" aria-label={t('songbook.close')}
                onClick={() => setModalOpen(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <label className="form-label">
                  <span style={{ display: 'block', marginBottom: 4, fontSize: '0.85rem', color: 'var(--text-dim)' }}>
                    {t('songbook.colTitle')} *
                  </span>
                  <input className="form-input" value={draft.title}
                    onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))} autoFocus />
                </label>
                <label className="form-label">
                  <span style={{ display: 'block', marginBottom: 4, fontSize: '0.85rem', color: 'var(--text-dim)' }}>
                    {t('songbook.colArtist')}
                  </span>
                  <input className="form-input" value={draft.artist || ''}
                    onChange={(e) => setDraft((d) => ({ ...d, artist: e.target.value }))} />
                </label>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <label className="form-label" style={{ flex: 1, minWidth: 120 }}>
                    <span style={{ display: 'block', marginBottom: 4, fontSize: '0.85rem', color: 'var(--text-dim)' }}>
                      {t('songbook.colDuration')}
                    </span>
                    <input className="form-input" placeholder="m:ss" value={draft.duration || ''}
                      onChange={(e) => setDraft((d) => ({ ...d, duration: e.target.value }))} />
                  </label>
                  <label className="form-label" style={{ flex: 1, minWidth: 120 }}>
                    <span style={{ display: 'block', marginBottom: 4, fontSize: '0.85rem', color: 'var(--text-dim)' }}>
                      {t('songbook.colKey')}
                    </span>
                    <input className="form-input" value={draft.keys || ''}
                      onChange={(e) => setDraft((d) => ({ ...d, keys: e.target.value }))} />
                  </label>
                  <label className="form-label" style={{ flex: 1, minWidth: 120 }}>
                    <span style={{ display: 'block', marginBottom: 4, fontSize: '0.85rem', color: 'var(--text-dim)' }}>
                      {t('songbook.colTempo')}
                    </span>
                    <input className="form-input" value={draft.tempo || ''}
                      onChange={(e) => setDraft((d) => ({ ...d, tempo: e.target.value }))} />
                  </label>
                </div>
                <label className="form-label">
                  <span style={{ display: 'block', marginBottom: 4, fontSize: '0.85rem', color: 'var(--text-dim)' }}>
                    {t('songbook.colNotes')}
                  </span>
                  <AutoTextarea className="form-input" rows={3} value={draft.notes || ''}
                    onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))} />
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setModalOpen(false)}>
                {t('songbook.cancel')}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={saveDraft}
                disabled={saving || !draft.title.trim()}
              >
                {saving ? t('songbook.saving') : t('songbook.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Charts panel — list the per-instrument charts of the selected song */}
      {selectedSong && (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-label={t('songCharts.panelTitle', { title: selectedSong.title || '—' })}
          onClick={(e) => { if (e.target === e.currentTarget && !cSaving) closeCharts(); }}
        >
          <div className="modal" style={{ maxWidth: 640 }}>
            <div className="modal-header">
              <h3 className="modal-title">{t('songCharts.panelTitle', { title: selectedSong.title || '—' })}</h3>
              <button type="button" className="modal-close" aria-label={t('songCharts.close')}
                onClick={closeCharts}>✕</button>
            </div>
            <div className="modal-body">
              {openChartsList.length === 0 && (
                <p style={{ color: 'var(--text-dim)' }}>{t('songCharts.noCharts')}</p>
              )}

              {openChartsList.map((chart) => (
                <div
                  key={`${chart.instrument}-${chart.id}`}
                  className="card"
                  style={{ padding: '10px 14px', margin: '6px 0' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                    <strong>{chart.instrument}</strong>
                    {// A2-CAP: content.songbook
  canWriteContent && (
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-small btn-secondary" onClick={() => startEditChart(chart)}>
                          {t('songCharts.edit')}
                        </button>
                        <button className="btn btn-small btn-danger" onClick={() => deleteChart(chart)}>
                          {t('songCharts.delete')}
                        </button>
                      </div>
                    )}
                  </div>
                  {chart.content && (
                    <pre style={{ margin: '6px 0 0', whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: '0.9rem', color: 'var(--text-dim)' }}>
                      {chart.content}
                    </pre>
                  )}
                </div>
              ))}

              {// A2-CAP: content.songbook
  canWriteContent && (
                <>
                  {cShowForm ? (
                    <div className="card" style={{ padding: 14, marginTop: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <strong>{cMode === 'edit' ? t('songCharts.editChart') : t('songCharts.addChart')}</strong>
                        <button type="button" className="btn btn-small btn-secondary" onClick={() => setCShowForm(false)}>
                          {t('songCharts.cancel')}
                        </button>
                      </div>
                      <div className="form-group">
                        <label>{t('songCharts.instrument')} *</label>
                        <input
                          className="form-input"
                          list="chart-instrument-suggestions"
                          value={cInstrument}
                          placeholder={t('songCharts.instrumentPh')}
                          onChange={(e) => setCInstrument(e.target.value)}
                          disabled={cMode === 'edit'}
                        />
                        <datalist id="chart-instrument-suggestions">
                          {chartInstruments.map((inst) => <option key={inst} value={inst} />)}
                        </datalist>
                      </div>
                      <div className="form-group" style={{ marginTop: 8 }}>
                        <label>{t('songCharts.content')}</label>
                        <AutoTextarea
                          className="form-input"
                          rows={2}
                          value={cContent}
                          placeholder={t('songCharts.contentPh')}
                          onChange={(e) => setCContent(e.target.value)}
                        />
                      </div>
                      {cErr && <div style={{ color: 'var(--danger)', fontSize: '0.9rem', marginTop: 8 }}>{cErr}</div>}
                      <button
                        type="button"
                        className="btn btn-primary"
                        style={{ marginTop: 12 }}
                        onClick={saveChart}
                        disabled={cSaving || !cInstrument.trim()}
                      >
                        {cSaving ? t('songCharts.saving') : t('songCharts.save')}
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-primary"
                      style={{ marginTop: 10 }}
                      onClick={startAddChart}
                    >
                      {t('songCharts.addChart')}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
